import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Table, Card, message, Spin, Select, Button, Tooltip } from 'antd';
import { DownOutlined, RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../utils/exceptionUtils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';
import TimeRangeFilter, { TimeRange } from '../../../components/dashboard/TimeRangeFilter';
import {
    DASHBOARD_PACIFIC_TZ,
    dashboardNowInPacific,
    isDashboardAvgColumnLabel,
    resolveDashboardComparisonAvgLabel,
    formatHubDisplayLabel,
} from '../../../utils/dashboardPacificTime';
import MetricTooltip from '../../../components/dashboard/MetricTooltip';
import { QUERY_SORTING_DASHBOARD } from '../../../graphql/queries/dashboard/querySortingDashboard';
import { QUERY_SORTING_DASHBOARD_V2 } from '../../../graphql/queries/dashboard/querySortingDashboardV2';
import {
    DASHBOARD_EXCLUDE_HUB_LABELS,
    useOperableHubSelectOptions,
} from '../../../hooks/useOperableHubSelectOptions';
import {
    SortingDashboardItem,
    SortingDashboardAggregateItem,
    SortingDashboardCustomerSlice,
    QuerySortingDashboardResponse,
    WarehouseBusinessType,
} from '../../../graphql/types/dashboard/SortingDashboardTypes';
import {
    GENERATE_DASHBOARD_TABLE_EXPORT,
    GenerateDashboardTableExportResponse,
    GenerateDashboardTableExportVariables,
} from '../../../graphql/mutations/dashboard/generateDashboardTableExport';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
    useAsyncReportExportCooldown,
} from '../../../components/asyncReportDownload';
import { ScaleQueryType } from '../../../graphql/types/dashboard/ScaleDashboardTypes';
import SortingDashboardDetailDrawer, {
    SortingDashboardDrillType,
} from './components/SortingDashboardDetailDrawer';
import {
    CHILD_DRILL_GROUP_TITLE,
    getSortingDashboardVariantConfig,
} from './sortingDashboardVariantConfig';
import {
    mergeSortingDashboardItemsForDisplay,
    mergedHub0MetricRowForDisplay,
    weightedNumeratorForSortingRate,
    WRONG_SORT_SUBMETRIC_RATE_FIELDS,
} from './sortingDashboardMetricAggregation';
import { SortingOrderFlowSegmented } from './components/SortingOrderFlowSegmented';
import {
    getSortingHubNextDayHandoverTooltip,
    getSortingHubFinalNotHandoverTooltip,
    SORTING_HUB_HANDOVER_SPLIT_CODES,
    SORTING_HUB_BIZ_TYPE_OPTIONS,
    SORTING_HUB_SLICE_CUSTOMER_NAMES,
    SORTING_HUB_SLICE_CUSTOMER_FBT,
    SORTING_HUB_SLICE_CUSTOMER_CBT,
    FBT_SAME_DAY_HANDOVER_TOOLTIP,
    CBT_NEXT_DAY_HANDOVER_TOOLTIP,
    type SortingDashboardBusinessTypeTab,
} from './sortingDashboardOrderFlowConstants';
import MetricAnnotationCell from '../../../components/dashboardAnnotation/MetricAnnotationCell';
import { useDashboardAnnotations } from '../../../components/dashboardAnnotation/useDashboardAnnotations';
import { buildAnnotationKey } from '../../../components/dashboardAnnotation/buildAnnotationKey';
import {
    dashboardAnnotationRegistry,
    PAGE_KEY_SORTING,
    type CellContext,
    type DashboardAnnotationColumnType,
} from '../../../components/dashboardAnnotation/registry';
import DashboardMarkLegend from '../../../components/dashboard/DashboardMarkLegend';
import { useDashboardMarkRules } from '../../../hooks/useDashboardMarkRules';
import { DASHBOARD_MARK_CONFIG_KEY } from '../../../graphql/types/dashboard/DashboardMarkRuleTypes';
import {
    appendTargetValueDesc,
    CellMark,
    markCellClassName,
    NO_CELL_MARK,
    resolveCellMark,
} from '../../../utils/dashboardMarkRule';

/** 图例中的指标简称。仅这三个指标参与标记，与后端 dashboard.mark.sorting 配置一一对应 */
const SORTING_MARK_METRIC_LABELS: Record<string, string> = {
    ascanMissRate: 'ASCAN漏扫率',
    transferArrivalMissRate: '转运到仓漏扫率',
    wrongSortToWrongLineABCount: '错分至错误路线(A+B)',
};


/** 近2期请求用：周区间按 ISO 周（周一为起）算，与 TimeRangeFilter 近3周/上周一致 */
dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);


const LOCAL_NAMED_CUSTOMERS = ['Temu半托管', 'TikTok CBT', 'TikTok FBT', '出口易'] as const;
const CROSS_NAMED_CUSTOMERS = ['Temu全托管', '顺丰', 'WANB', '京东物流', '急速国际2', '汇发', 'CNE递一', 'TikTok GS'] as const;
const SORTING_CUSTOMER_NAMES = [...LOCAL_NAMED_CUSTOMERS, ...CROSS_NAMED_CUSTOMERS] as const;

/** 安全解析周期字符串为美西日期，避免 Invalid time value；解析失败返回 null */
function safeParsePeriodInTz(periodStr: string, tz: string): dayjs.Dayjs | null {
    try {
        const d = dayjs.tz(periodStr, tz);
        return d != null && d.isValid() ? d : null;
    } catch {
        return null;
    }
}

// 格式化数字：整数不带小数，小数保留2位，添加千分位分隔符
const formatNumber = (num: number | undefined | null): string => {
    if (num === undefined || num === null || isNaN(num)) {
        return '0';
    }

    // 判断是否为整数
    if (Number.isInteger(num)) {
        // 整数：只添加千分位分隔符，不显示小数
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // 小数：四舍五入到2位小数
    const rounded = Math.round(num * 100) / 100;

    // 格式化为固定2位小数
    const fixed = rounded.toFixed(2);

    // 分离整数和小数部分
    const [integerPart, decimalPart] = fixed.split('.');

    // 为整数部分添加千分位分隔符
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 组合整数和小数部分
    return `${formattedInteger}.${decimalPart}`;
};

// 百分比与环比展示统一保留两位小数（% 与 pp）
/** 子指标件数占比：错分 A/B/C/(A+B) 分母为 inboundWrongSortDenominatorCount（见 countOverInboundAsRateString） */
const SHARE_OF_INBOUND_COUNT_FIELDS = new Set<string>([
    'wrongSortToWrongLineABCount',
    'wrongSortToWrongHubACount',
    'wrongSortToWrongWhsBCount',
    'wrongSortToWrongLineCCount',
]);

function countOverInboundAsRateString(
    item: Partial<SortingDashboardItem & SortingDashboardAggregateItem>,
    field: string
): string | null {
    const useWrongSortDenom = WRONG_SORT_SUBMETRIC_RATE_FIELDS.has(field);
    const denom = useWrongSortDenom
        ? Number(item.inboundWrongSortDenominatorCount ?? 0)
        : Number(item.inboundCount ?? 0);
    if (denom === 0) return null;
    let count: number;
    if (field === 'wrongSortToWrongLineABCount') {
        count =
            Number(item.wrongSortToWrongHubACount ?? 0) + Number(item.wrongSortToWrongWhsBCount ?? 0);
    } else {
        const raw = item[field as keyof typeof item];
        count = Number(raw ?? 0);
    }
    if (count === 0) return '0.00%';
    const pct = (count / denom) * 100;
    const scaled = Math.round(pct * 1e10) / 1e10;
    const trimmed = scaled.toFixed(10).replace(/\.?0+$/, '');
    return `${trimmed}%`;
}

/**
 * 多日「日均」回退：用 ∑分子/∑分母 换算为率，避免对每日率做算术平均（入库量日际波动时后者不准）。
 * 与 mergeSortingDashboardItemsForDisplay/表格 ∑分子÷∑分母 的展示口径一致。
 */
const RATE_WEIGHTED_NUM_DEN: Record<
    string,
    {
        numerators: (keyof SortingDashboardItem)[];
        denominator: keyof SortingDashboardItem | (keyof SortingDashboardItem)[];
    }
> = {
    wrongSortRate: { numerators: ['wrongSortCount'], denominator: 'inboundWrongSortDenominatorCount' },
    wrongSortToWrongHubACount: {
        numerators: ['wrongSortToWrongHubACount'],
        denominator: 'inboundWrongSortDenominatorCount',
    },
    wrongSortToWrongWhsBCount: {
        numerators: ['wrongSortToWrongWhsBCount'],
        denominator: 'inboundWrongSortDenominatorCount',
    },
    wrongSortToWrongLineCCount: {
        numerators: ['wrongSortToWrongLineCCount'],
        denominator: 'inboundWrongSortDenominatorCount',
    },
    wrongSortToWrongLineABCount: {
        numerators: ['wrongSortToWrongHubACount', 'wrongSortToWrongWhsBCount'],
        denominator: 'inboundWrongSortDenominatorCount',
    },
    ascanMissRate: { numerators: ['ascanMissCount'], denominator: 'dscanCount' },
    outbound12hRate: { numerators: [] as (keyof SortingDashboardItem)[], denominator: 'inboundCount' },
    shouldArriveNotArriveRate: { numerators: ['shouldArriveNotArriveCount'], denominator: 'inboundCount' },
    outboundMissRate: { numerators: ['outboundMissCount'], denominator: 'inboundCount' },
    transferArrivalMissRate: {
        numerators: ['transferArrivalMissCount'],
        denominator: 'transferArrivalDscanCount',
    },
    lostDamagedRate: { numerators: ['lostDamagedCount'], denominator: 'inboundCount' },
    /** 次日交接率：nextDayHandoverCount / inboundCount，与后端一致 */
    nextDayHandoverRate: { numerators: ['nextDayHandoverCount'], denominator: 'inboundCount' },
    /** 跨境客户24小时上网率：crossBorderOnline24hCount / crossBorderHandoverCount */
    crossBorderOnline24hRate: {
        numerators: ['crossBorderOnline24hCount'],
        denominator: 'crossBorderHandoverCount',
    },
    /** 整体漏扫率：口径对齐后端 (ascanMiss + transferArrivalMiss) / (dscan + transferArrivalDscan)；
     *  单期展示直接读后端字符串，此处仅供日均/周均/月均加权与环比 pp 使用 */
    overallMissScanRate: {
        numerators: ['ascanMissCount', 'transferArrivalMissCount'],
        denominator: ['dscanCount', 'transferArrivalDscanCount'],
    },
};

const formatPercentage = (rate?: string | null, metricKey?: string): string => {
    if (!rate || rate === '-') return '-';

    // 如果是百分比字符串（如"95.5%"），提取数字部分
    const numStr = rate.replace('%', '');
    const num = parseFloat(numStr);

    if (isNaN(num)) return rate;

    void metricKey;
    const decimalPlaces = 2;
    const multiplier = Math.pow(10, decimalPlaces);

    // 四舍五入
    const rounded = Math.round(num * multiplier) / multiplier;
    return `${rounded.toFixed(decimalPlaces)}%`;
};

// 格式化环比变化：率类为百分点(pp)，固定保留 2 位小数
const formatChange = (change?: string, metricType?: string): string => {
    if (!change || change === '-') return '-';

    const isPositive = change.startsWith('+');
    const isNegative = change.startsWith('-');
    const isPp = change.endsWith('pp');

    // 提取数字部分（去除+/-、%和pp）
    const numStr = change.replace(/[+\-%]/g, '').replace(/pp$/i, '').trim();
    const num = parseFloat(numStr);

    if (isNaN(num)) {
        return change;
    }

    void metricType;
    const decimals = 2;
    const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);

    // 率类环比为百分点(x.xxpp)，数值类为百分比(x.xx%)
    const sign = isPositive ? '+' : isNegative ? '-' : '';
    return isPp ? `${sign}${rounded.toFixed(decimals)}pp` : `${sign}${rounded.toFixed(decimals)}%`;
};

interface TableDataRow {
    key: string;
    metricName: string;
    metricTooltip?: string; // 指标提示文本
    isExpandable: boolean;
    isDetail: boolean;
    depth: number;
    sortingCode?: string;
    metricType:
        | 'inbound'
        | 'shouldArriveNotArriveRate'
        | 'outbound12hRate'
        | 'ascanMissRate'
        | 'transferArrivalMissRate'
        | 'outboundMissRate'
        | 'wrongSortRate'
        | 'wrongSortToWrongLineABCount'
        | 'wrongSortToWrongHubACount'
        | 'wrongSortToWrongWhsBCount'
        | 'wrongSortToWrongLineCCount'
        | 'lostDamagedRate'
        | 'sortEfficiency'
        | 'sortPeak'
        | 'nextDayHandoverRate'
        | 'fbtSameDayHandoverRate'
        | 'cbtNextDayHandoverRate'
        | 'crossBorderOnline24hRate'
        | 'overallMissScanRate'
        | 'shouldArriveNotArriveCount'
        | 'notCorrectlyPackedCount'
        | 'bagLabelNotShippedCount'
        | 'noDepartureActionCount';
    /** 覆盖 common_config 阈值 fieldName */
    thresholdFieldKey?: string;
    /** 展开子行构建时写入：下钻请求优先用该 hubId（含 0=全美），避免仅依赖 sortingCode 映射或单 Hub 兜底 */
    drillHubId?: number;
    /** FBT/CBT 拆行专属：下钻时强制传该 customerName（DB 名），覆盖筛选器 selectedCustomerName */
    drillCustomerName?: string;
    [key: string]: any; // 动态的时间周期字段
}

/** 判断该时间周期是否在最近30天内（仅该周期内的数据支持明细下钻）；与页面一致按美西时区计算 */
function isPeriodWithinLast30Days(period: string, queryType: string): boolean {
    if (period === '环比' || isDashboardAvgColumnLabel(period)) {
        return false;
    }
    const todayPacific = dayjs().tz(DASHBOARD_PACIFIC_TZ).startOf('day');
    const thresholdStr = todayPacific.subtract(30, 'day').format('YYYY-MM-DD');
    if (queryType === ScaleQueryType.DAILY) {
        return period >= thresholdStr && dayjs(period, 'YYYY-MM-DD', true).isValid();
    }
    if (period.includes('~')) {
        const [, endStr] = period.split('~');
        const endStrTrim = endStr?.trim() ?? '';
        return endStrTrim >= thresholdStr && dayjs(endStrTrim, 'YYYY-MM-DD', true).isValid();
    }
    const month = dayjs(period, 'YYYY-MM', true);
    if (!month.isValid()) return false;
    const lastDayStr = month.endOf('month').format('YYYY-MM-DD');
    return lastDayStr >= thresholdStr;
}

/** 表格单元格：「数（率）」混排不换；纯百分比用后端的「高精度」串，界面用两位小数展示 */
function isPlainBackendPercentCellValue(value: unknown): value is string {
    return typeof value === 'string' && !value.includes('（') && /%\s*$/.test(value.trim());
}

/** 分拣看板导出：纯百分比单元格收成两位小数，与表格展示一致（不共用全局 export 格式化，以免影响断更等页） */
function sortingDashboardExportCellString(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
    if (typeof v === 'string') {
        if (isPlainBackendPercentCellValue(v)) {
            const num = parseFloat(v.replace(/,/g, '').replace(/%/g, '').trim());
            if (!Number.isNaN(num)) {
                const decimalPlaces = 2;
                const multiplier = 10 ** decimalPlaces;
                const rounded = Math.round(num * multiplier) / multiplier;
                return `${rounded.toFixed(decimalPlaces)}%`;
            }
        }
        return v;
    }
    return '';
}

/** 将时间周期转为下钻查询的日期范围 { start, end } */
function periodToDateRange(period: string, queryType: string): { start: string; end: string } {
    if (queryType === ScaleQueryType.DAILY) {
        return { start: period, end: period };
    }
    if (period.includes('~')) {
        const [start, end] = period.split('~').map(s => s?.trim() ?? '');
        return { start: start || period, end: end || period };
    }
    const month = dayjs(period, 'YYYY-MM', true);
    if (month.isValid()) {
        return {
            start: month.startOf('month').format('YYYY-MM-DD'),
            end: month.endOf('month').format('YYYY-MM-DD'),
        };
    }
    return { start: period, end: period };
}

/** 下钻：0 与等价于 0 的展示（如 0%、0.0）不可点；分拣/质量页共用 */
function isNonZeroForDrill(value: unknown): boolean {
    if (value === '' || value === null || value === undefined || value === '-') {
        return false;
    }
    if (typeof value === 'number') {
        return !Number.isNaN(value) && value !== 0;
    }
    if (typeof value === 'string') {
        const t = value.trim();
        if (t === '' || t === '-') return false;
        const n = parseFloat(t.replace(/%/g, '').replace(/,/g, ''));
        if (Number.isNaN(n)) return false;
        return n !== 0;
    }
    return false;
}

/**
 * 看板指标注释：columnType 只依赖 period 是否为环比/日均标签；dateKey 额外依赖 queryType——
 * DAILY 视图「周环比」锚定单日 endDate（T-7 对比，与 startDate 无关），WEEKLY/MONTHLY 视图「环比」是两段 range 对比，range 才是正确签名
 */
function computeAnnotationKey(
    period: string,
    timeRange: TimeRange | null
): { columnType: DashboardAnnotationColumnType; dateKey: string } {
    const rangeDateKey = `${timeRange?.startDate ?? ''}~${timeRange?.endDate ?? ''}`;
    const queryType = timeRange?.queryType ?? ScaleQueryType.DAILY;
    if (period === '环比') {
        return { columnType: 'WOW', dateKey: queryType === ScaleQueryType.DAILY ? (timeRange?.endDate ?? '') : rangeDateKey };
    }
    if (isDashboardAvgColumnLabel(period)) {
        return { columnType: 'AVG', dateKey: rangeDateKey };
    }
    const { start, end } = periodToDateRange(period, queryType);
    return { columnType: 'RAW', dateKey: start === end ? start : `${start}~${end}` };
}

const sortingAnnotationConfig = dashboardAnnotationRegistry[PAGE_KEY_SORTING];

const SortingSharedPage: React.FC = () => {
    const { t } = useTranslation();
    const { exportLoading, runExportWithCooldown, getExportButtonLabel } = useAsyncReportExportCooldown();
    const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
    // 默认展开所有指标
    const [expandedMetrics, setExpandedMetrics] = useState<Set<string>>(new Set());
    /** 上次用于自动展开的 expandableKeys，用于区分「数据变更」与「用户全部折叠」 */
    const prevExpandableKeysRef = useRef<string[]>([]);
    const [selectedHubId, setSelectedHubId] = useState<number>(0); // 默认为0，表示全部
    const [businessTypeTab, setBusinessTypeTab] = useState<SortingDashboardBusinessTypeTab>('lastMileHub');
    const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [drawerTitleText, setDrawerTitleText] = useState('');
    const [drawerDrillType, setDrawerDrillType] = useState<SortingDashboardDrillType | null>(null);
    /** 打开抽屉时传入的 hubId：全美行传 0，具体 hub 行传该行对应的 hubId，避免始终用 selectedHubId 导致全美时点具体 hub 也传 0 */
    const [drawerHubId, setDrawerHubId] = useState<number>(0);
    /** 下钻抽屉的统计日期范围（点击某列时设为该周期，仅最近30天内的周期可点） */
    const [drawerStatDateStart, setDrawerStatDateStart] = useState<string>('');
    const [drawerStatDateEnd, setDrawerStatDateEnd] = useState<string>('');
    /** 下钻抽屉的小时窗：跨午夜窗口下需按"被点击的日期"裁剪，单日/全天保持原值 */
    const [drawerWindowStartHour, setDrawerWindowStartHour] = useState<number>(0);
    const [drawerWindowEndHour, setDrawerWindowEndHour] = useState<number>(23);
    const [drawerCustomerName, setDrawerCustomerName] = useState<string | null>(null);
    const [drawerSortingCode, setDrawerSortingCode] = useState<string | null>(null);
    const [downloadCompleteModalVisible, setDownloadCompleteModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const [generateDashboardTableExport] = useMutation<
        GenerateDashboardTableExportResponse,
        GenerateDashboardTableExportVariables
    >(GENERATE_DASHBOARD_TABLE_EXPORT);

    const {
        hubOptions,
        hubListForDrawer,
        loading: hubsLoading,
        hasFullWarehouseAccess,
        singleAccessibleHubId,
    } = useOperableHubSelectOptions({
        excludeLabels: [...DASHBOARD_EXCLUDE_HUB_LABELS],
        filterBusinessTypes: [
            businessTypeTab === 'sortingHub' ? WarehouseBusinessType.SORTING : WarehouseBusinessType.LAST_MILE,
        ],
    });

    useEffect(() => {
        if (hubsLoading || hubOptions.length === 0) return;
        if (singleAccessibleHubId != null) {
            setSelectedHubId(singleAccessibleHubId);
            return;
        }
        if (!hubOptions.some((o) => o.value === selectedHubId)) {
            setSelectedHubId(hubOptions[0]?.value ?? 0);
        }
    }, [hubsLoading, hubOptions, singleAccessibleHubId, selectedHubId]);

    const hubSelectValue = useMemo(() => {
        if (hubsLoading) return undefined;
        return hubOptions.some((o) => o.value === selectedHubId) ? selectedHubId : undefined;
    }, [hubsLoading, hubOptions, selectedHubId]);

    const customerNameOptions = useMemo(() => {
        if (businessTypeTab === 'sortingHub') {
            return SORTING_HUB_BIZ_TYPE_OPTIONS.map((o) => ({ ...o }));
        }
        const options: { value: string | null; label: string }[] = [{ value: null, label: '全部' }];
        SORTING_CUSTOMER_NAMES.forEach((name) => options.push({ value: name, label: name }));
        options.push({ value: '__other__', label: '其他客户' });
        return options;
    }, [businessTypeTab]);

    // 切换 tab 时清空客户/业务类型选择：两 tab 选项集不同，串味会带无效值
    useEffect(() => {
        setSelectedCustomerName(null);
    }, [businessTypeTab]);

    // 主请求：当前周期；昨日/上周/上月时再请求近2期用于前端计算环比（揽收分拣+尾程）
    // 仅在有小时过滤（非全天 00-23）或今日实时时走 V2（新小时表）；
    // 全天 0-23 走 V1（旧日级表，有完整历史数据）
    const isUsingV2 = timeRange?.queryType === ScaleQueryType.DAILY && (
        timeRange.todayRealtime === true ||
        (timeRange.startHour ?? 0) !== 0 ||
        (timeRange.endHour ?? 23) !== 23
    );
    const currentBusinessType =
        businessTypeTab === 'sortingHub' ? WarehouseBusinessType.SORTING : WarehouseBusinessType.LAST_MILE;
    // 分拣仓 tab 附带 customerNames，触发后端按 customer_name 批切片返回，供 4 仓 FBT/CBT 拆行使用；
    // 用户已选具体客户时不传，让后端按 customerName 单值过滤，主 items[] 与"入库包裹量"跟随筛选收窄
    const sliceCustomerNames: string[] | undefined =
        businessTypeTab === 'sortingHub'
        && (selectedCustomerName == null || selectedCustomerName === '__other__')
            ? [...SORTING_HUB_SLICE_CUSTOMER_NAMES]
            : undefined;
    const { data, loading: mainLoading } = useQuery<QuerySortingDashboardResponse>(
        isUsingV2 ? QUERY_SORTING_DASHBOARD_V2 : QUERY_SORTING_DASHBOARD,
        {
            variables: {
                input: isUsingV2 ? {
                    queryType: timeRange?.queryType as any,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    startHour: timeRange?.startHour ?? 0,
                    endHour: timeRange?.endHour ?? 23,
                    todayRealtime: timeRange?.todayRealtime ?? false,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                } : {
                    queryType: timeRange?.queryType as any,
                    startDate: timeRange?.startDate,
                    endDate: timeRange?.endDate,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                },
            },
            skip: !timeRange,
            onError: (error) => {
                console.error('查询分拣看板失败:', error);
                message.error('查询分拣数据失败，请稍后重试');
            },
        }
    );

    const rawListFromMain = data?.querySortingDashboard?.items ?? [];
    const rawListForPeriodDetection = rawListFromMain;
    const rawList = rawListFromMain;
    const rawListForHubExtras = rawListFromMain;
    const loading = mainLoading;
    const latestCalculatedAt = data?.querySortingDashboard?.latestCalculatedAt ?? null;

    // 昨日/上周/上月或自定义范围不足7天/2周/2月时请求 data2；日始终请求，周不足2周、月不足2月也请求；多期时 data2 基于 data1 最后有数据周期
    const { isSinglePeriod, twoPeriodTimeRange } = useMemo(() => {
        if (!timeRange) return { isSinglePeriod: false, twoPeriodTimeRange: null as TimeRange | null };
        const start = safeParsePeriodInTz(timeRange.startDate, DASHBOARD_PACIFIC_TZ);
        const end = safeParsePeriodInTz(timeRange.endDate, DASHBOARD_PACIFIC_TZ);
        if (!start || !end) return { isSinglePeriod: false, twoPeriodTimeRange: null };
        const qt = timeRange.queryType as ScaleQueryType;
        const daysInRange = end.diff(start, 'day') + 1;
        const monthsInRange = (end.year() - start.year()) * 12 + (end.month() - start.month()) + 1;

        const isSingle =
            (qt === ScaleQueryType.DAILY && start.isSame(end, 'day')) ||
            (qt === ScaleQueryType.WEEKLY && daysInRange <= 7) ||
            (qt === ScaleQueryType.MONTHLY && start.isSame(end, 'month'));

        // 日始终请求 data2；周不足 2 周、月不足 2 月也请求（含自定义时间范围）
        const needTwoPeriods =
            isSingle ||
            qt === ScaleQueryType.DAILY ||
            (qt === ScaleQueryType.WEEKLY && daysInRange < 14) ||
            (qt === ScaleQueryType.MONTHLY && monthsInRange < 2);

        if (!needTwoPeriods) return { isSinglePeriod: false, twoPeriodTimeRange: null };

        let start2: string;
        let end2: string;
        if (qt === ScaleQueryType.DAILY) {
            if (isSingle) {
                // 昨日周环比与近3日/近7日一致：均为 今日 vs T-7，故 data2 只请求 T-7 单日
                const tMinus7 = start.subtract(7, 'day').format('YYYY-MM-DD');
                start2 = tMinus7;
                end2 = tMinus7;
            } else {
                const periodsFromData1 = Array.from(
                    new Set<string>(rawListForPeriodDetection.map((i: SortingDashboardItem) => i.timePeriod))
                )
                    .filter((p: string) => !isDashboardAvgColumnLabel(p))
                    .sort();
                if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
                const lastDayWithData = periodsFromData1[periodsFromData1.length - 1];
                const parsedDay = safeParsePeriodInTz(lastDayWithData, DASHBOARD_PACIFIC_TZ);
                if (!parsedDay) {
                    const tMinus7 = end.subtract(7, 'day').format('YYYY-MM-DD');
                    start2 = tMinus7;
                    end2 = tMinus7;
                } else {
                    const tMinus7 = parsedDay.subtract(7, 'day').format('YYYY-MM-DD');
                    start2 = tMinus7;
                    end2 = tMinus7;
                }
            }
        } else if (qt === ScaleQueryType.WEEKLY) {
            if (isSingle) {
                const prevWeekStart = start.subtract(1, 'week').startOf('isoWeek');
                start2 = prevWeekStart.format('YYYY-MM-DD');
                end2 = prevWeekStart.endOf('isoWeek').format('YYYY-MM-DD');
            } else {
                const periodsFromData1 = Array.from(
                    new Set<string>(rawListForPeriodDetection.map((i: SortingDashboardItem) => i.timePeriod))
                )
                    .filter((p: string) => !isDashboardAvgColumnLabel(p))
                    .sort();
                if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
                const lastPeriodWithData = periodsFromData1[periodsFromData1.length - 1];
                const parsed = safeParsePeriodInTz(lastPeriodWithData, DASHBOARD_PACIFIC_TZ);
                if (!parsed) {
                    start2 = start.subtract(daysInRange, 'day').format('YYYY-MM-DD');
                    end2 = start.subtract(1, 'day').format('YYYY-MM-DD');
                } else {
                    const lastWeekStart = parsed.startOf('isoWeek');
                    const prevWeekStart = lastWeekStart.subtract(1, 'week');
                    start2 = prevWeekStart.format('YYYY-MM-DD');
                    end2 = prevWeekStart.endOf('isoWeek').format('YYYY-MM-DD');
                }
            }
        } else {
            if (isSingle) {
                const prevMonthStart = start.subtract(1, 'month').startOf('month');
                start2 = prevMonthStart.format('YYYY-MM-DD');
                end2 = prevMonthStart.endOf('month').format('YYYY-MM-DD');
            } else {
                const periodsFromData1 = Array.from(
                    new Set<string>(rawListForPeriodDetection.map((i: SortingDashboardItem) => i.timePeriod))
                )
                    .filter((p: string) => !isDashboardAvgColumnLabel(p))
                    .sort();
                if (periodsFromData1.length === 0) return { isSinglePeriod: false, twoPeriodTimeRange: null };
                const lastPeriodWithData = periodsFromData1[periodsFromData1.length - 1];
                const monthStr = /^\d{4}-\d{2}$/.test(lastPeriodWithData) ? `${lastPeriodWithData}-01` : lastPeriodWithData;
                const parsed = safeParsePeriodInTz(monthStr, DASHBOARD_PACIFIC_TZ);
                if (!parsed) {
                    start2 = start.subtract(monthsInRange, 'month').startOf('month').format('YYYY-MM-DD');
                    end2 = start.subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
                } else {
                    const lastMonthStart = parsed.startOf('month');
                    const prevMonthStart = lastMonthStart.subtract(1, 'month');
                    start2 = prevMonthStart.format('YYYY-MM-DD');
                    end2 = prevMonthStart.endOf('month').format('YYYY-MM-DD');
                }
            }
        }
        // data2 走 V2 路径时需要小时窗对齐主查询，否则环比基数不一致（T 小时窗 vs T-7 全天）
        // todayRealtime 不能传给 data2（data2 查的是历史日期），改为传 0~最后完整小时。
        // 「最后完整小时」不能用 dashboardNowInPacific().hour()：ETL 整点结束才写入，当前小时数仓里没有行，
        // data1 实际拉不到，但 data2（T-7 已是完整历史）会拉到 → 环比偏低。
        // 从 latestCalculatedAt 反推 = updatedAt.hour() - 1，两边对齐。
        // 跨天场景（updatedAt 落在 0 点）反推得 -1，说明当天无完整小时可对比，不请求 data2，环比自然显示 -。
        let data2StartHour: number | undefined;
        let data2EndHour: number | undefined;
        if (timeRange.todayRealtime) {
            // latestCalculatedAt 是 Instant.toString()（ISO-8601 带 Z），dayjs(x) 会按 UTC 正确解析，
            // 再 .tz(Pacific) 得到美西小时；不要改成 dayjs.tz(x, zone)——那会丢弃 Z 后缀把时刻塞回美西 wall-clock。
            const alignedEndHour = latestCalculatedAt
                ? dayjs(latestCalculatedAt).tz(DASHBOARD_PACIFIC_TZ).hour() - 1
                : null;
            if (alignedEndHour == null || alignedEndHour < 0) {
                return { isSinglePeriod: isSingle, twoPeriodTimeRange: null };
            }
            data2StartHour = 0;
            data2EndHour = alignedEndHour;
        } else {
            data2StartHour = timeRange.startHour;
            data2EndHour = timeRange.endHour;
        }
        return {
            isSinglePeriod: isSingle,
            twoPeriodTimeRange: {
                queryType: timeRange.queryType,
                startDate: start2,
                endDate: end2,
                startHour: data2StartHour,
                endHour: data2EndHour,
                todayRealtime: false,
            },
        };
    }, [timeRange, rawListForPeriodDetection, latestCalculatedAt]);

    // data2 与主查询走同一链路：主查询走 V1 则 data2 也走 V1，避免环比请求误走 V2
    const isData2UsingV2 = isUsingV2 && twoPeriodTimeRange != null;
    const { data: data2 } = useQuery<QuerySortingDashboardResponse>(
        isData2UsingV2 ? QUERY_SORTING_DASHBOARD_V2 : QUERY_SORTING_DASHBOARD,
        {
            variables: {
                input: isData2UsingV2 ? {
                    queryType: twoPeriodTimeRange?.queryType as any,
                    startDate: twoPeriodTimeRange?.startDate,
                    endDate: twoPeriodTimeRange?.endDate,
                    startHour: twoPeriodTimeRange?.startHour ?? 0,
                    endHour: twoPeriodTimeRange?.endHour ?? 23,
                    todayRealtime: twoPeriodTimeRange?.todayRealtime ?? false,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                } : {
                    queryType: twoPeriodTimeRange?.queryType as any,
                    startDate: twoPeriodTimeRange?.startDate,
                    endDate: twoPeriodTimeRange?.endDate,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                },
            },
            skip: !twoPeriodTimeRange,
        }
    );

    const handleTimeRangeChange = useCallback((newTimeRange: TimeRange) => {
        setTimeRange(newTimeRange);
    }, []);

    const handleHubChange = useCallback((value: number) => {
        setSelectedHubId(value);
    }, []);

    /** 当前账号仅可访问一个 Hub 时，下钻/接口用该 id，避免下拉仍为「全部」时误传 0 */
    const singleAccessibleHubIdForDrill = singleAccessibleHubId;

    const baseConfig = useMemo(() => getSortingDashboardVariantConfig(businessTypeTab), [businessTypeTab]);

    /** 仅当选中「完整一天 00-23」时展示分拣时效/峰值；非完整一天或「今日实时」均隐藏 */
    const shouldHideSortingEfficiencyMetrics = useMemo(() => {
        if (!timeRange || timeRange.queryType !== ScaleQueryType.DAILY) return false;
        if (timeRange.todayRealtime === true) return true;
        const startH = timeRange.startHour ?? 0;
        const endH = timeRange.endHour ?? 23;
        return !(startH === 0 && endH === 23);
    }, [timeRange]);

    const { drillMetricTypes, topLevelMetricConfigs, wrongSortChildConfigs, overallMissScanChildConfigs, finalNotHandoverChildConfigs } =
        useMemo(() => {
            if (shouldHideSortingEfficiencyMetrics) {
                return {
                    ...baseConfig,
                    topLevelMetricConfigs: baseConfig.topLevelMetricConfigs.filter(
                        config => config.key !== 'sortEfficiency' && config.key !== 'sortPeak'
                    ),
                };
            }
            return baseConfig;
        }, [baseConfig, shouldHideSortingEfficiencyMetrics]);

    // 切换展开状态
    const toggleExpand = (key: string) => {
        setExpandedMetrics(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const queryType = timeRange?.queryType ?? ScaleQueryType.DAILY;

    const rawList2 = data2?.querySortingDashboard?.items ?? [];
    const getMetricFieldValue = useCallback((item: Partial<SortingDashboardItem & SortingDashboardAggregateItem>, field: string): unknown => {
        switch (field) {
            default:
                if (SHARE_OF_INBOUND_COUNT_FIELDS.has(field)) {
                    return countOverInboundAsRateString(item, field);
                }
                return item[field as keyof typeof item];
        }
    }, []);
    const SORTING_FIELDS = [
        'inboundCount',
        'inboundWrongSortDenominatorCount',
        'shouldArriveNotArriveRate',
        'outbound12hRate',
        'ascanMissRate',
        'transferArrivalMissRate',
        'outboundMissRate',
        'wrongSortRate',
        'wrongSortCount',
        'wrongSortToWrongLineABCount',
        'wrongSortToWrongHubACount',
        'wrongSortToWrongWhsBCount',
        'wrongSortToWrongLineCCount',
        'lostDamagedRate',
        'lostDamagedCount',
        'sortEfficiency',
        'sortPeak',
        'overallMissScanRate',
        'nextDayHandoverRate',
        'crossBorderOnline24hRate',
        'shouldArriveNotArriveCount',
        'notCorrectlyPackedCount',
        'bagLabelNotShippedCount',
        'noDepartureActionCount',
    ] as const;
    const RATE_FIELDS_FOR_PP = new Set<string>([
        'shouldArriveNotArriveRate',
        'outbound12hRate',
        'ascanMissRate',
        'transferArrivalMissRate',
        'outboundMissRate',
        'wrongSortRate',
        'wrongSortToWrongLineABCount',
        'wrongSortToWrongHubACount',
        'wrongSortToWrongWhsBCount',
        'wrongSortToWrongLineCCount',
        'lostDamagedRate',
        'overallMissScanRate',
        'nextDayHandoverRate',
        'crossBorderOnline24hRate',
    ]);
    const data2ForChange = useMemo((): Map<string, string> => {
        const map = new Map<string, string>();
        // 日粒度单周期（昨日）：环比用后端 T/T-7，不用近2期前端计算
        if (queryType === ScaleQueryType.DAILY && isSinglePeriod) return map;
        if (rawList2.length === 0) return map;
        const periods2 = Array.from(new Set(rawList2.map((i: SortingDashboardItem) => i.timePeriod)))
            .filter((p) => !isDashboardAvgColumnLabel(p))
            .sort();
        if (periods2.length < 2) return map;
        const pLast = periods2[periods2.length - 1];
        const pPrev = periods2[periods2.length - 2];
        const byKeyPeriod = new Map<string, Map<string, number>>();
        /** 率类环比：∑分子/∑分母 再比 pp，避免同一周期多行时把「率」当数相加（错分率、断更72h 等） */
        const byKeyPeriodWeighted = new Map<string, Map<string, { n: number; d: number }>>();
        rawList2.forEach((item: SortingDashboardItem) => {
            const rowKey = String(item.hubId) === '0' ? '0' : (item.sortingCode ?? '');
            if (!rowKey) return;
            SORTING_FIELDS.forEach((field) => {
                const period = item.timePeriod;
                if (period !== pLast && period !== pPrev) return;

                const wspec = RATE_WEIGHTED_NUM_DEN[field];
                if (wspec != null && RATE_FIELDS_FOR_PP.has(field)) {
                    const den = Array.isArray(wspec.denominator)
                        ? wspec.denominator.reduce((s, k) => s + Number(item[k] ?? 0), 0)
                        : Number(item[wspec.denominator] ?? 0);
                    const num = weightedNumeratorForSortingRate(item, field, wspec.numerators);
                    const wkey = `${rowKey}_${field}`;
                    if (!byKeyPeriodWeighted.has(wkey)) byKeyPeriodWeighted.set(wkey, new Map());
                    const wPer = byKeyPeriodWeighted.get(wkey)!;
                    const acc = wPer.get(period) ?? { n: 0, d: 0 };
                    wPer.set(period, { n: acc.n + num, d: acc.d + den });
                    return;
                }

                const v = getMetricFieldValue(item, field);
                let num: number | null = null;
                if (typeof v === 'number' && !isNaN(v)) num = v;
                else if (typeof v === 'string' && v !== '' && v !== '-') {
                    const parsed = parseFloat(v.replace('%', ''));
                    if (!isNaN(parsed)) num = parsed;
                }
                if (num === null) return;
                const key = `${rowKey}_${field}`;
                if (!byKeyPeriod.has(key)) byKeyPeriod.set(key, new Map());
                const perPeriod = byKeyPeriod.get(key)!;
                perPeriod.set(period, (perPeriod.get(period) ?? 0) + num);
            });
        });
        const ppDecimalsForField = (_keyField: string) => 2;
        const pooledPct = (per: Map<string, { n: number; d: number }>, p: string): number => {
            const o = per.get(p);
            if (!o || o.d === 0) return 0;
            return (o.n / o.d) * 100;
        };
        byKeyPeriodWeighted.forEach((perPeriod, key) => {
            const underscoreIdx = key.indexOf('_');
            const keyField = underscoreIdx >= 0 ? key.slice(underscoreIdx + 1) : '';
            const vCurr = pooledPct(perPeriod, pLast);
            const vPrev = pooledPct(perPeriod, pPrev);
            const diff = vCurr - vPrev;
            const dec = ppDecimalsForField(keyField);
            const rounded = Math.round(diff * Math.pow(10, dec)) / Math.pow(10, dec);
            map.set(key, rounded >= 0 ? `+${rounded.toFixed(dec)}pp` : `${rounded.toFixed(dec)}pp`);
        });
        byKeyPeriod.forEach((perPeriod, key) => {
            const underscoreIdx = key.indexOf('_');
            const keyField = underscoreIdx >= 0 ? key.slice(underscoreIdx + 1) : '';
            const isRate = RATE_FIELDS_FOR_PP.has(keyField);
            const vCurr = perPeriod.get(pLast) ?? 0;
            const vPrev = perPeriod.get(pPrev) ?? 0;
            if (isRate) {
                const diff = vCurr - vPrev;
                const dec = ppDecimalsForField(keyField);
                const rounded = Math.round(diff * Math.pow(10, dec)) / Math.pow(10, dec);
                map.set(key, rounded >= 0 ? `+${rounded.toFixed(dec)}pp` : `${rounded.toFixed(dec)}pp`);
            } else {
                if (vPrev === 0) {
                    map.set(key, vCurr > 0 ? '+100.00%' : '0.00%');
                    return;
                }
                const change = ((vCurr - vPrev) / vPrev) * 100;
                map.set(key, change >= 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`);
            }
        });
        return map;
    }, [rawList2, queryType, isSinglePeriod, getMetricFieldValue]);

    /** sortingCode -> hubId，用于点击某 hub 行打开抽屉时传入正确的 hubId */
    const sortingCodeToHubId = useMemo(() => {
        const m = new Map<string, number>();
        rawListForHubExtras.forEach((item: SortingDashboardItem) => {
            if (item.sortingCode && String(item.hubId) !== '0') {
                m.set(item.sortingCode, Number(item.hubId));
            }
        });
        return m;
    }, [rawListForHubExtras]);

    // 昨日/上周/上月快捷项：聚合列分别为「上周同日」「上周」「上月」（按美西日期匹配）
    const avgLabel = useMemo(() => {
        if (!timeRange) {
            return queryType === ScaleQueryType.DAILY ? '日均' : queryType === ScaleQueryType.WEEKLY ? '周均' : '月均';
        }
        return resolveDashboardComparisonAvgLabel(
            timeRange.queryType,
            timeRange.startDate,
            timeRange.endDate,
            timeRange.todayRealtime
        );
    }, [timeRange, queryType]);

    const allSortingCodesForExpansion = useMemo(() => {
        const allSortingCodesSet = new Set(
            rawListForHubExtras
                .filter(item => {
                    const hubId = String(item.hubId);
                    return hubId !== '0' && item.sortingCode && item.sortingCode !== 'US';
                })
                .map(item => item.sortingCode!)
        );
        return Array.from(allSortingCodesSet).sort((a, b) => {
            const inboundA = rawListForHubExtras
                .filter(i => i.sortingCode === a)
                .reduce((sum, i) => sum + Number(i.inboundCount), 0);
            const inboundB = rawListForHubExtras
                .filter(i => i.sortingCode === b)
                .reduce((sum, i) => sum + Number(i.inboundCount), 0);
            return inboundB - inboundA;
        });
    }, [rawListForHubExtras]);

    // tableData 的指标 tooltip 要拼接目标值，故须在其之前取到规则
    const markCtx = useDashboardMarkRules(
        DASHBOARD_MARK_CONFIG_KEY.SORTING,
        queryType === ScaleQueryType.DAILY
    );

    // 转换数据结构：行是指标，列是时间 + 日均/周均/月均 + 环比
    // 表格周期列与数值仅用主查询 data1（rawList），避免多期时 data2 与主范围重叠导致某日翻倍（与规模看板一致）
    // data2 仅用于环比（data2ForChange / prevValueByKeyForSingle），不参与表头与单元格数据
    const { tableData, timePeriods } = useMemo(() => {

        if (!rawList || rawList.length === 0) return { tableData: [], timePeriods: [] };

        const listForPeriods = rawList;
        const periods = Array.from(new Set(listForPeriods.map(item => item.timePeriod)))
            .filter((p) => !isDashboardAvgColumnLabel(p))
            .sort();
        const dataByPeriod: Record<string, SortingDashboardItem[]> = {};
        periods.forEach(period => {
            dataByPeriod[period] = listForPeriods.filter(item => item.timePeriod === period);
        });

        const prevPeriods: string[] = [];
        const prevDataByPeriod: Record<string, SortingDashboardItem[]> = {};
        const pLast = periods[periods.length - 1];
        const pPrev = periods.length >= 2 ? periods[periods.length - 2] : '';
        const parsedLast = safeParsePeriodInTz(pLast, DASHBOARD_PACIFIC_TZ);
        const tMinus7Period = parsedLast ? parsedLast.subtract(7, 'day').format('YYYY-MM-DD') : '';
        /** 日粒度：仅当可比 T-7（表内列或 data2 含 T-7 单日）时算周环比，绝不退化为「最后一列 vs 倒数第二列」 */
        const dailyT7Comparable =
            queryType === ScaleQueryType.DAILY &&
            Boolean(tMinus7Period) &&
            (periods.includes(tMinus7Period) ||
                rawList2.some((i: SortingDashboardItem) => i.timePeriod === tMinus7Period));
        const useTwoPeriodsForChange =
            (isSinglePeriod && rawList2.length > 0) ||
            (queryType === ScaleQueryType.DAILY && dailyT7Comparable) ||
            (queryType !== ScaleQueryType.DAILY && periods.length >= 2);
        /** 日：仅 T-7 在表内时用该列；T-7 仅来自 data2 时留空由 getPrevValForChange 走 prevValueByKeyForT7。周/月仍用倒数第二列 */
        const prevPeriodForChange = useTwoPeriodsForChange
            ? periods.length >= 2
                ? queryType === ScaleQueryType.DAILY
                    ? tMinus7Period && periods.includes(tMinus7Period)
                        ? tMinus7Period
                        : ''
                    : pPrev
                : ''
            : '';

        // 单周期时 data2 含上期+本期，只取「上期」按 rowKey 汇总成「上期值」供环比用，不展示为表格列
        const prevValueByKeyForSingle = (() => {
            if (!isSinglePeriod || rawList2.length === 0) return new Map<string, Record<string, unknown>>();
            const periods2 = Array.from(new Set(rawList2.map((i: SortingDashboardItem) => i.timePeriod)))
                .filter((p) => !isDashboardAvgColumnLabel(p))
                .sort();
            const prevPeriodOnly = periods2[0] ?? null;
            if (prevPeriodOnly == null) return new Map<string, Record<string, unknown>>();
            const m = new Map<string, Record<string, unknown>>();
            const itemsBySortingCodeSingle = new Map<string, SortingDashboardItem[]>();
            rawList2.forEach((item: SortingDashboardItem) => {
                if (item.timePeriod !== prevPeriodOnly) return;
                if (String(item.hubId) === '0') return;
                const rowKey = item.sortingCode ?? '';
                if (!rowKey) return;
                if (!itemsBySortingCodeSingle.has(rowKey)) itemsBySortingCodeSingle.set(rowKey, []);
                itemsBySortingCodeSingle.get(rowKey)!.push(item);
            });
            itemsBySortingCodeSingle.forEach((items, rowKey) => {
                const merged = mergeSortingDashboardItemsForDisplay(items);
                const row: Record<string, unknown> = {};
                SORTING_FIELDS.forEach((field) => {
                    const v = getMetricFieldValue(merged, field);
                    if (v !== undefined && v !== null && v !== '' && v !== '-') {
                        row[field] = v;
                    }
                });
                m.set(rowKey, row);
            });
            const hub0MergedSingle = mergedHub0MetricRowForDisplay(
                rawList2.filter(
                    (i) => i.timePeriod === prevPeriodOnly && String(i.hubId) === '0'
                ),
                getMetricFieldValue,
                SORTING_FIELDS
            );
            if (Object.keys(hub0MergedSingle).length > 0) {
                m.set('0', hub0MergedSingle);
            }
            // 全美(0) 若 data2 未带上期 sortEfficiency/sortPeak，用上期各 hub 按 inbound 加权补全，避免环比回退逻辑
            const row0 = m.get('0');
            if (row0) {
                const prevItems = rawList2.filter((i: SortingDashboardItem) => i.timePeriod === prevPeriodOnly && String(i.hubId) !== '0' && i.sortingCode);
                const totalInbound = prevItems.reduce((s, i) => s + Number(i.inboundCount || 0), 0);
                if (totalInbound > 0) {
                    if (row0.sortEfficiency === undefined || row0.sortEfficiency === null || row0.sortEfficiency === '') {
                        const weighted = prevItems.reduce((s, i) => s + Number(i.sortEfficiency || 0) * Number(i.inboundCount || 0), 0) / totalInbound;
                        row0.sortEfficiency = weighted;
                    }
                    if (row0.sortPeak === undefined || row0.sortPeak === null || row0.sortPeak === '') {
                        const weightedPeak = prevItems.reduce((s, i) => s + Number(i.sortPeak || 0) * Number(i.inboundCount || 0), 0) / totalInbound;
                        row0.sortPeak = weightedPeak;
                    }
                }
            }
            return m;
        })();

        // 日多期且 T-7 不在表内时（如近7日），用 data2 的 T-7 单日按 rowKey+字段汇总：「最后一列 vs T-7」；无 T-7 数据则不展示周环比（不退化相邻日）
        const prevValueByKeyForT7 = ((): Map<string, Record<string, unknown>> | null => {
            if (isSinglePeriod || rawList2.length === 0 || queryType !== ScaleQueryType.DAILY || !tMinus7Period || periods.includes(tMinus7Period)) return null;
            const m = new Map<string, Record<string, unknown>>();
            const itemsBySortingCodeT7 = new Map<string, SortingDashboardItem[]>();
            rawList2.forEach((item: SortingDashboardItem) => {
                if (item.timePeriod !== tMinus7Period) return;
                if (String(item.hubId) === '0') return;
                const rowKey = item.sortingCode ?? '';
                if (!rowKey) return;
                if (!itemsBySortingCodeT7.has(rowKey)) itemsBySortingCodeT7.set(rowKey, []);
                itemsBySortingCodeT7.get(rowKey)!.push(item);
            });
            itemsBySortingCodeT7.forEach((items, rowKey) => {
                const merged = mergeSortingDashboardItemsForDisplay(items);
                const row: Record<string, unknown> = {};
                SORTING_FIELDS.forEach((field) => {
                    const v = getMetricFieldValue(merged, field);
                    if (v !== undefined && v !== null && v !== '' && v !== '-') {
                        row[field] = v;
                    }
                });
                m.set(rowKey, row);
            });
            const hub0MergedT7 = mergedHub0MetricRowForDisplay(
                rawList2.filter(
                    (i) => i.timePeriod === tMinus7Period && String(i.hubId) === '0'
                ),
                getMetricFieldValue,
                SORTING_FIELDS
            );
            if (Object.keys(hub0MergedT7).length > 0) {
                m.set('0', hub0MergedT7);
            }
            const row0 = m.get('0');
            if (row0) {
                const prevItems = rawList2.filter((i: SortingDashboardItem) => i.timePeriod === tMinus7Period && String(i.hubId) !== '0' && i.sortingCode);
                const totalInbound = prevItems.reduce((s, i) => s + Number(i.inboundCount || 0), 0);
                if (totalInbound > 0) {
                    if (row0.sortEfficiency === undefined || row0.sortEfficiency === null || row0.sortEfficiency === '') {
                        const weighted = prevItems.reduce((s, i) => s + Number(i.sortEfficiency || 0) * Number(i.inboundCount || 0), 0) / totalInbound;
                        row0.sortEfficiency = weighted;
                    }
                    if (row0.sortPeak === undefined || row0.sortPeak === null || row0.sortPeak === '') {
                        const weightedPeak = prevItems.reduce((s, i) => s + Number(i.sortPeak || 0) * Number(i.inboundCount || 0), 0) / totalInbound;
                        row0.sortPeak = weightedPeak;
                    }
                }
            }
            return m;
        })();

        // 环比：数值类 (Y-X)/X*100 → +(-)x.x%；率类 Y-X（百分点）→ +(-)x.xpp
        const getChangeFromTwoValues = (curr: any, prev: any, isRate: boolean, _ppDecimals: number = 1): string => {
            const toNum = (x: any): number | null => {
                if (x == null || x === '' || x === '-') return null;
                if (typeof x === 'number' && !isNaN(x)) return x;
                if (typeof x === 'string') {
                    const n = parseFloat(x.replace(/%|,/g, '').trim());
                    return isNaN(n) ? null : n;
                }
                return null;
            };
            const vCurr = toNum(curr);
            const vPrev = toNum(prev);
            if (vCurr === null) return '-';
            // 对比期缺数据（null/''/'-'）与对比期真为 0 必须分开：前者无基数可算，不能编成 +100%
            if (vPrev === null) return '-';
            if (isRate) {
                const diff = vCurr - vPrev;
                const ppDecimals = 2;
                const rounded = Math.round(diff * Math.pow(10, ppDecimals)) / Math.pow(10, ppDecimals);
                return rounded >= 0 ? `+${rounded.toFixed(ppDecimals)}pp` : `${rounded.toFixed(ppDecimals)}pp`;
            }
            if (vPrev === 0) return vCurr > 0 ? '+100.00%' : '0.00%';
            const change = ((vCurr - vPrev) / vPrev) * 100;
            return change >= 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;
        };

        const getChangeForRowFromData2 = (rowKey: string, field: string, fallback: string): string =>
            data2ForChange.get(`${rowKey}_${field}`) ?? fallback;

        /** 单周期：data2 上期；日多期：表内 T-7 列或 data2 的 T-7 汇总；周/月：prevPeriodForChange 列 */
        const getPrevValForChange = (rowKey: string, field: string, row: Record<string, unknown>): unknown => {
            if (isSinglePeriod) return prevValueByKeyForSingle.get(rowKey)?.[field];
            if (queryType === ScaleQueryType.DAILY) {
                if (prevValueByKeyForT7 != null && prevValueByKeyForT7.has(rowKey)) {
                    return prevValueByKeyForT7.get(rowKey)?.[field];
                }
                return prevPeriodForChange ? row[prevPeriodForChange] : undefined;
            }
            return prevPeriodForChange ? row[prevPeriodForChange] : undefined;
        };

        const filterByHubId = (hubId: number) =>
            hubId === 0 ? (item: SortingDashboardItem) => String(item.hubId) === '0' : (item: SortingDashboardItem) => item.hubId === hubId;

        /** 日均/周均/月均由主查询 items 汇总计算，环比在 buildMetricRow 中单独用 getPrevValForChange 计算，不使用后端 aggregate */
        const getMetricAvgFromItems = (
            opts: { hubId: number; sortingCode?: string },
            config: { key: string; field: string; isRate: boolean; isPlaceholder?: boolean }
        ): { avg: number | string; change: string } => {
            if (config.isPlaceholder) {
                return { avg: '-', change: '-' };
            }
            // 单周期（昨日/上周/上月/今日实时）：avgLabel 列展示对比期数据（来自 data2），而非当期均值。
            // data2 缺该期数据时必须返回 '-'：单期下当期均值恒等于当期列，退化会让「上周同期」显示成当期值
            if (isSinglePeriod) {
                const rowKey =
                    opts.sortingCode != null && opts.sortingCode !== ''
                        ? opts.sortingCode
                        : String(opts.hubId);
                const prevRow = prevValueByKeyForSingle.get(rowKey);
                const rawVal = prevRow?.[config.field];
                if (rawVal === undefined || rawVal === null || rawVal === '' || rawVal === '-') {
                    return { avg: '-', change: '-' };
                }
                if (config.isRate) return { avg: String(rawVal), change: '-' };
                if (config.key === 'sortEfficiency') return { avg: formatNumber(Math.round(Number(rawVal))), change: '-' };
                return { avg: Number(rawVal), change: '-' };
            }
            const filterFn =
                opts.sortingCode != null && opts.sortingCode !== ''
                    ? (item: SortingDashboardItem) => item.sortingCode === opts.sortingCode
                    : filterByHubId(opts.hubId);
            return calculateAvgAndChange(
                filterFn,
                config.field,
                config.isRate,
                config.key === 'sortEfficiency',
                false
            );
        };

        // 计算某个hub/sortingCode在某个指标的均值和环比
        const calculateAvgAndChange = (
            filterFn: (item: SortingDashboardItem) => boolean,
            field: string,
            isRate: boolean,
            isEfficiency: boolean = false,
            isPlaceholder: boolean = false // 是否为占位字段（如错分率）
        ): { avg: number | string; change: string } => {
            if (isPlaceholder) {
                return { avg: '-', change: '-' };
            }

            const weightedSpec = isRate ? RATE_WEIGHTED_NUM_DEN[field] : undefined;
            if (weightedSpec) {
                let sumNum = 0;
                let sumDen = 0;
                let hasWeighted = false;
                periods.forEach(period => {
                    const items = dataByPeriod[period].filter(filterFn);
                    if (items.length === 0) return;
                    hasWeighted = true;
                    const merged = mergeSortingDashboardItemsForDisplay(items);
                    sumDen += Array.isArray(weightedSpec.denominator)
                        ? weightedSpec.denominator.reduce((s, k) => s + Number(merged[k] ?? 0), 0)
                        : Number(merged[weightedSpec.denominator] ?? 0);
                    sumNum += weightedNumeratorForSortingRate(merged, field, weightedSpec.numerators);
                });
                if (!hasWeighted || sumDen === 0) {
                    return { avg: '-', change: '-' };
                }
                const currentAvg = (sumNum / sumDen) * 100;
                const rounded = Math.round(currentAvg * 100) / 100;
                return {
                    avg: formatPercentage(`${rounded}%`, field),
                    change: '-',
                };
            }

            const currentValues: number[] = [];
            let hasAnyCurrentValue = false;

            periods.forEach(period => {
                const items = dataByPeriod[period].filter(filterFn);
                if (items.length > 0) {
                    const merged = mergeSortingDashboardItemsForDisplay(items);
                    const value = getMetricFieldValue(merged, field);
                    if (value !== null && value !== undefined && value !== '' && value !== '-') {
                        hasAnyCurrentValue = true;
                        if (isRate) {
                            if (typeof value === 'string') {
                                const num = parseFloat(value.replace('%', ''));
                                if (!isNaN(num)) currentValues.push(num);
                            }
                        } else {
                            const num = Number(value);
                            if (!isNaN(num)) currentValues.push(num);
                        }
                    }
                }
            });

            if (!hasAnyCurrentValue || currentValues.length === 0) {
                return { avg: '-', change: '-' };
            }

            const prevValues: number[] = [];
            prevPeriods.forEach(period => {
                const items = prevDataByPeriod[period].filter(filterFn);
                if (items.length > 0) {
                    const merged = mergeSortingDashboardItemsForDisplay(items);
                    const value = getMetricFieldValue(merged, field);

                    if (value !== null && value !== undefined && value !== '' && value !== '-') {
                        if (isRate) {
                            if (typeof value === 'string') {
                                const num = parseFloat(value.replace('%', ''));
                                if (!isNaN(num)) prevValues.push(num);
                            }
                        } else {
                            const num = Number(value);
                            if (!isNaN(num)) prevValues.push(num);
                        }
                    }
                }
            });

            const currentAvg = currentValues.length > 0
                ? currentValues.reduce((sum, v) => sum + v, 0) / currentValues.length
                : 0;

            const prevAvg = prevValues.length > 0
                ? prevValues.reduce((sum, v) => sum + v, 0) / prevValues.length
                : 0;

            let change = '-';
            if (isRate) {
                const diff = currentAvg - prevAvg;
                const rounded = Math.round(diff * 100) / 100;
                change = rounded >= 0 ? `+${rounded}pp` : `${rounded}pp`;
            } else if (prevAvg !== 0 && currentAvg !== 0) {
                const pct = (currentAvg - prevAvg) / prevAvg * 100;
                const rounded = Math.round(pct * 100) / 100;
                const sign = rounded >= 0 ? '+' : '';
                change = `${sign}${rounded}%`;
            }

            let avgResult: number | string;
            if (isRate) {
                const rounded = Math.round(currentAvg * 100) / 100;
                avgResult = formatPercentage(`${rounded}%`, field);
            } else if (isEfficiency) {
                avgResult = formatNumber(Math.round(currentAvg));
            } else {
                avgResult = Math.round(currentAvg);
            }

            return { avg: avgResult, change };
        };

        const buildMetricRow = (
            rowKey: string,
            metricName: string,
            metricType: TableDataRow['metricType'],
            field: string,
            isRate: boolean,
            depth: number,
            options?: {
                metricTooltip?: string;
                sortingCode?: string;
                isExpandable?: boolean;
                hubId?: number;
            }
        ): TableDataRow => {
            const row: TableDataRow = {
                key: rowKey,
                metricName,
                metricTooltip: options?.metricTooltip,
                isExpandable: options?.isExpandable ?? false,
                isDetail: depth > 0,
                depth,
                sortingCode: options?.sortingCode,
                metricType,
                ...(options?.hubId !== undefined ? { drillHubId: options.hubId } : {}),
            };
            periods.forEach(period => {
                const items = dataByPeriod[period];
                const matched =
                    options?.sortingCode == null
                        ? items.filter((i) => String(i.hubId) === String(options?.hubId ?? 0))
                        : items.filter((i) => i.sortingCode === options.sortingCode);
                const item =
                    matched.length === 0 ? undefined : mergeSortingDashboardItemsForDisplay(matched);
                const value = item == null ? null : getMetricFieldValue(item, field);
                if (value === '' || value === '-' || value == null) {
                    row[period] = '-';
                } else if (isRate) {
                    // 保留接口/derive 高精度率串：渲染层 formatPercentage 展示两位小数；下钻用 isNonZeroForDrill(原始值)
                    row[period] = String(value);
                } else if (field === 'sortEfficiency') {
                    row[period] = formatNumber(Math.round(Number(value)));
                } else {
                    row[period] = Number(value);
                }
            });
            const hubId = Number(options?.hubId ?? 0);
            const { avg } = getMetricAvgFromItems({ hubId, sortingCode: options?.sortingCode }, {
                key: metricType,
                field,
                isRate,
            });
            row[avgLabel] = avg;
            row['环比'] = useTwoPeriodsForChange
                ? getChangeFromTwoValues(
                    row[pLast],
                    getPrevValForChange(options?.sortingCode ?? String(hubId), field, row),
                    isRate
                )
                : getChangeForRowFromData2(options?.sortingCode ?? String(hubId), field, '-');
            return row;
        };

        // 分拣仓 tab 下 4 个仓（LAXSCBT/LAXS/EWRS/HOUS）把「次日交接率」拆两行展示的构造器；
        // 数据取自主/对比查询的 customerSlices，按 nextDayHandoverCount/inboundCount 现算率，
        // 单元格用「率 / 数」组合字符串（渲染层原样输出，不再走 formatPercentage）
        const sliceByName = new Map<string, SortingDashboardCustomerSlice>();
        const sliceByName2 = new Map<string, SortingDashboardCustomerSlice>();
        if (businessTypeTab === 'sortingHub') {
            (data?.querySortingDashboard?.customerSlices ?? []).forEach((s) => {
                if (s.customerName) sliceByName.set(s.customerName, s);
            });
            (data2?.querySortingDashboard?.customerSlices ?? []).forEach((s) => {
                if (s.customerName) sliceByName2.set(s.customerName, s);
            });
        }
        const computeSliceRate = (
            items: SortingDashboardItem[] | undefined,
            sortingCode: string,
            period: string
        ): { num: number; den: number; pct: number } | null => {
            if (!items || items.length === 0) return null;
            const matched = items.filter((i) => i.sortingCode === sortingCode && i.timePeriod === period);
            if (matched.length === 0) return null;
            const num = matched.reduce((s, i) => s + Number(i.nextDayHandoverCount ?? 0), 0);
            const den = matched.reduce((s, i) => s + Number(i.inboundCount ?? 0), 0);
            if (den === 0) return null;
            return { num, den, pct: (num / den) * 100 };
        };
        const formatBizCell = (v: { den: number; pct: number } | null): string => {
            if (!v) return '-';
            const rounded = Math.round(v.pct * 100) / 100;
            return `${rounded.toFixed(2)}% / ${formatNumber(v.den)}`;
        };
        const buildBizTypeHandoverRow = (
            bizType: 'FBT' | 'CBT',
            label: string,
            tooltip: string,
            sortingCode: string,
            hubId: number,
            baseKey: string
        ): TableDataRow => {
            const metricType: TableDataRow['metricType'] =
                bizType === 'FBT' ? 'fbtSameDayHandoverRate' : 'cbtNextDayHandoverRate';
            const customerName =
                bizType === 'FBT' ? SORTING_HUB_SLICE_CUSTOMER_FBT : SORTING_HUB_SLICE_CUSTOMER_CBT;
            // LAXSCBT 走全客户合计（见下文 useMainAggregate），下钻也不硬绑 customer_name，
            // 让抽屉沿用筛选器的选项，避免明细口径与聚合口径不一致。
            const drillCustomerName =
                sortingCode === 'LAXSCBT' && bizType === 'CBT' ? undefined : customerName;
            const row: TableDataRow = {
                key: `${metricType}-${baseKey}`,
                metricName: label,
                metricTooltip: tooltip,
                isExpandable: false,
                isDetail: true,
                depth: 1,
                sortingCode,
                metricType,
                drillHubId: hubId,
                ...(drillCustomerName !== undefined ? { drillCustomerName } : {}),
            };
            // 业务类型筛选与当前拆行不一致时（如选 FBT 时看 CBT 行），全列填 '-'，避免筛选器与拆行数据来源不联动
            const filterMismatch =
                selectedCustomerName != null
                && selectedCustomerName !== '__other__'
                && selectedCustomerName !== customerName;
            if (filterMismatch) {
                periods.forEach((period) => {
                    row[period] = '-';
                });
                row[avgLabel] = '-';
                row['环比'] = '-';
                return row;
            }
            const slice = sliceByName.get(customerName);
            const slice2 = sliceByName2.get(customerName);
            // 两种情况改从主 items[] 取数（切片不可用或语义不同）：
            // 1. LAXSCBT-CBT：channel 拆的 TikTok CBT / FBT 都算 CBT，按全客户合计口径；
            // 2. 用户按具体客户过滤时：后端已按 customerName 收窄主 items[]，无需切片。
            const useMainAggregate =
                (sortingCode === 'LAXSCBT' && bizType === 'CBT')
                || (selectedCustomerName != null && selectedCustomerName !== '__other__');
            const sourceItems = useMainAggregate
                ? data?.querySortingDashboard?.items
                : slice?.items;
            const sourceItems2 = useMainAggregate
                ? data2?.querySortingDashboard?.items
                : slice2?.items;
            periods.forEach((period) => {
                row[period] = formatBizCell(computeSliceRate(sourceItems, sortingCode, period));
            });
            // avgLabel 列
            if (isSinglePeriod) {
                const periods2 = Array.from(
                    new Set((sourceItems2 ?? []).map((i) => i.timePeriod))
                )
                    .filter((p) => !isDashboardAvgColumnLabel(p))
                    .sort();
                const prevOnly = periods2[0];
                row[avgLabel] = prevOnly
                    ? formatBizCell(computeSliceRate(sourceItems2, sortingCode, prevOnly))
                    : '-';
            } else {
                let totalNum = 0;
                let totalDen = 0;
                let has = false;
                periods.forEach((period) => {
                    const v = computeSliceRate(sourceItems, sortingCode, period);
                    if (v) {
                        totalNum += v.num;
                        totalDen += v.den;
                        has = true;
                    }
                });
                if (!has || totalDen === 0) {
                    row[avgLabel] = '-';
                } else {
                    const pct = (totalNum / totalDen) * 100;
                    const rounded = Math.round(pct * 100) / 100;
                    row[avgLabel] = `${rounded.toFixed(2)}% / ${formatNumber(totalDen)}`;
                }
            }
            // 环比：当期率 - 上期率（pp），缺一即 '-'
            if (useTwoPeriodsForChange) {
                const currV = computeSliceRate(sourceItems, sortingCode, pLast);
                let prevV: { num: number; den: number; pct: number } | null = null;
                if (isSinglePeriod) {
                    const periods2 = Array.from(
                        new Set((sourceItems2 ?? []).map((i) => i.timePeriod))
                    )
                        .filter((p) => !isDashboardAvgColumnLabel(p))
                        .sort();
                    const prevOnly = periods2[0];
                    if (prevOnly) prevV = computeSliceRate(sourceItems2, sortingCode, prevOnly);
                } else if (queryType === ScaleQueryType.DAILY) {
                    if (tMinus7Period && periods.includes(tMinus7Period)) {
                        prevV = computeSliceRate(sourceItems, sortingCode, tMinus7Period);
                    } else if (tMinus7Period) {
                        prevV = computeSliceRate(sourceItems2, sortingCode, tMinus7Period);
                    }
                } else if (pPrev) {
                    prevV = computeSliceRate(sourceItems, sortingCode, pPrev);
                }
                if (!currV || !prevV) {
                    row['环比'] = '-';
                } else {
                    const diff = currV.pct - prevV.pct;
                    const rounded = Math.round(diff * 100) / 100;
                    row['环比'] = rounded >= 0 ? `+${rounded.toFixed(2)}pp` : `${rounded.toFixed(2)}pp`;
                }
            } else {
                row['环比'] = '-';
            }
            return row;
        };

        const appendExpandedRows = (rows: TableDataRow[], baseKey: string, sortingCode: string | undefined, hubId: number) => {
            topLevelMetricConfigs.forEach(config => {
                const key = `${config.key}-${baseKey}`;
                // 分拣仓 tab 下 4 个仓的「次日交接率」拆成 FBT 当日 + CBT 次日 两行，跳过原单行渲染；
                // LAXSCBT 只做 CBT 业务、无 FBT 数据，仅展示 CBT 次日交接率一行（等价于原「次日交接率」）
                if (
                    businessTypeTab === 'sortingHub'
                    && sortingCode
                    && SORTING_HUB_HANDOVER_SPLIT_CODES.has(sortingCode)
                    && config.key === 'nextDayHandoverRate'
                ) {
                    if (sortingCode !== 'LAXSCBT') {
                        rows.push(
                            buildBizTypeHandoverRow(
                                'FBT',
                                'FBT 当日交接率',
                                FBT_SAME_DAY_HANDOVER_TOOLTIP,
                                sortingCode,
                                hubId,
                                baseKey
                            )
                        );
                    }
                    rows.push(
                        buildBizTypeHandoverRow(
                            'CBT',
                            'CBT 次日交接率',
                            CBT_NEXT_DAY_HANDOVER_TOOLTIP,
                            sortingCode,
                            hubId,
                            baseKey
                        )
                    );
                    return;
                }
                // 分拣仓 tab 下，「次日交接率 / 最终未交接率」按 hub 展示不同的完整口径（订单范围 + 完成交接节点）
                let effectiveTooltip = config.tooltip;
                if (businessTypeTab === 'sortingHub' && sortingCode) {
                    if (config.key === 'nextDayHandoverRate') {
                        effectiveTooltip = getSortingHubNextDayHandoverTooltip(sortingCode);
                    } else if (config.key === 'shouldArriveNotArriveRate') {
                        effectiveTooltip = getSortingHubFinalNotHandoverTooltip(sortingCode);
                    }
                }
                // LAXSTEMU 分拣仓「最终未交接包裹数」不按错误类型拆分，收起展开箭头
                const isGroup = Boolean((config as { isGroup?: boolean }).isGroup);
                const suppressExpand = sortingCode === 'LAXSTEMU' && config.key === 'shouldArriveNotArriveCount';
                const row = buildMetricRow(
                    key,
                    config.label,
                    config.key as TableDataRow['metricType'],
                    config.field,
                    config.isRate,
                    1,
                    {
                        metricTooltip: effectiveTooltip && appendTargetValueDesc(effectiveTooltip, markCtx, config.field),
                        sortingCode,
                        hubId,
                        isExpandable: isGroup && !suppressExpand,
                    }
                );
                rows.push(row);

                const isGroupExpanded = row.isExpandable && expandedMetrics.has(key);
                if (!isGroupExpanded) return;

                if (config.key === 'wrongSortRate') {
                    wrongSortChildConfigs.forEach(child => {
                        rows.push(
                            buildMetricRow(
                                `${child.key}-${baseKey}`,
                                child.label,
                                child.key as TableDataRow['metricType'],
                                child.field,
                                child.isRate,
                                2,
                                { sortingCode, hubId, metricTooltip: child.tooltip && appendTargetValueDesc(child.tooltip, markCtx, child.field) }
                            )
                        );
                    });
                }

                if (config.key === 'overallMissScanRate') {
                    overallMissScanChildConfigs.forEach(child => {
                        rows.push(
                            buildMetricRow(
                                `${child.key}-${baseKey}`,
                                child.label,
                                child.key as TableDataRow['metricType'],
                                child.field,
                                child.isRate,
                                2,
                                { sortingCode, hubId, metricTooltip: child.tooltip && appendTargetValueDesc(child.tooltip, markCtx, child.field) }
                            )
                        );
                    });
                }

                if (config.key === 'shouldArriveNotArriveCount') {
                    finalNotHandoverChildConfigs.forEach(child => {
                        rows.push(
                            buildMetricRow(
                                `${child.key}-${baseKey}`,
                                child.label,
                                child.key as TableDataRow['metricType'],
                                child.field,
                                child.isRate,
                                2,
                                { sortingCode, hubId, metricTooltip: child.tooltip && appendTargetValueDesc(child.tooltip, markCtx, child.field) }
                            )
                        );
                    });
                }

            });
        };

        const rows: TableDataRow[] = [];

        if (selectedHubId === 0 && hasFullWarehouseAccess) {
            const inboundMainRow: TableDataRow = {
                key: 'inbound-main',
                metricName: '全美入库包裹量',
                metricTooltip: '揽收或转运到达分拣仓的包裹数，按揽收入库扫描或转运到达扫描的首次日期归属',
                isExpandable: true,
                isDetail: false,
                depth: 0,
                metricType: 'inbound',
            };

            periods.forEach(period => {
                const items = dataByPeriod[period];
                const hub0Rows = items.filter((item) => String(item.hubId) === '0');
                const value =
                    hub0Rows.length === 0
                        ? 0
                        : hub0Rows.reduce((s, item) => s + Number(item.inboundCount ?? 0), 0);
                inboundMainRow[period] = value;
            });

            // 入库量也走 getMetricAvgFromItems，单期场景下 avgLabel 列取 data2 的上期值（T-7/上周/上月），
            // 否则会跟当期列同值，破坏「上周同日/上周/上月」语义
            const { avg } = getMetricAvgFromItems(
                { hubId: 0 },
                { key: 'inbound', field: 'inboundCount', isRate: false }
            );
            inboundMainRow[avgLabel] = avg;
            inboundMainRow['环比'] = useTwoPeriodsForChange
                ? getChangeFromTwoValues(inboundMainRow[pLast], getPrevValForChange('0', 'inboundCount', inboundMainRow), false)
                : getChangeForRowFromData2('0', 'inboundCount', '-');

            rows.push(inboundMainRow);

            if (expandedMetrics.has('inbound-main')) {
                appendExpandedRows(rows, 'main', undefined, 0);
            }
        }

        const allSortingCodesSet = new Set(
            rawList
                .filter(item => {
                    const hubId = String(item.hubId);
                    return hubId !== '0' && item.sortingCode && item.sortingCode !== 'US';
                })
                .map(item => item.sortingCode!)
        );
        const allSortingCodes = Array.from(allSortingCodesSet).sort((a, b) => {
            if (businessTypeTab === 'sortingHub') {
                const fixedOrder = ['LAXSCBT', 'LAXS', 'EWRS', 'LAXSTEMU'];
                const idxA = fixedOrder.indexOf(a);
                const idxB = fixedOrder.indexOf(b);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
            }
            const inboundA = rawList.filter(i => i.sortingCode === a).reduce((sum, i) => sum + Number(i.inboundCount), 0);
            const inboundB = rawList.filter(i => i.sortingCode === b).reduce((sum, i) => sum + Number(i.inboundCount), 0);
            return inboundB - inboundA;
        });

        allSortingCodes.forEach(sortingCode => {
            const sortingCodeKey = `inbound-${sortingCode}`;

            const sortingCodeInboundRow: TableDataRow = {
                key: sortingCodeKey,
                metricName: `${formatHubDisplayLabel(sortingCode)}入库包裹量`,
                isExpandable: true,
                isDetail: false,
                depth: 0,
                sortingCode,
                metricType: 'inbound',
            };

            periods.forEach(period => {
                const items = dataByPeriod[period];
                const matched = items.filter((i) => i.sortingCode === sortingCode);
                const value =
                    matched.length === 0
                        ? 0
                        : matched.reduce((s, i) => s + Number(i.inboundCount ?? 0), 0);
                sortingCodeInboundRow[period] = value;
            });

            // 同上：单期场景下 avgLabel 列读 data2，否则跟当期列同值
            const sortingCodeHubId = Number(rawList.find(i => i.sortingCode === sortingCode)?.hubId ?? 0);
            const { avg } = getMetricAvgFromItems(
                { hubId: sortingCodeHubId, sortingCode },
                { key: 'inbound', field: 'inboundCount', isRate: false }
            );
            sortingCodeInboundRow[avgLabel] = avg;
            sortingCodeInboundRow['环比'] = useTwoPeriodsForChange
                ? getChangeFromTwoValues(sortingCodeInboundRow[pLast], getPrevValForChange(sortingCode, 'inboundCount', sortingCodeInboundRow), false)
                : getChangeForRowFromData2(sortingCode, 'inboundCount', '-');

            rows.push(sortingCodeInboundRow);

            if (expandedMetrics.has(sortingCodeKey)) {
                const rowHubId = Number(rawList.find(i => i.sortingCode === sortingCode)?.hubId ?? 0);
                appendExpandedRows(rows, sortingCode, sortingCode, rowHubId);
            }
        });

        return { tableData: rows, timePeriods: [...periods, avgLabel, '环比'] };
    }, [
        rawList,
        rawList2,
        isSinglePeriod,
        data2ForChange,
        expandedMetrics,
        selectedHubId,
        avgLabel,
        queryType,
        getMetricFieldValue,
        topLevelMetricConfigs,
        wrongSortChildConfigs,
        overallMissScanChildConfigs,
        finalNotHandoverChildConfigs,
        businessTypeTab,
        data,
        data2,
        markCtx,
        selectedCustomerName,
    ]);

    /**
     * 红框需要「标记列的上周同日」。本看板默认范围较短（通常不含 T-8 / T-10），
     * 故对缺失的日期补一次查询，参数与主查询保持一致，仅替换日期范围。
     */
    const markLastWeekRange = useMemo(() => {
        if (!markCtx.enabled || markCtx.requiredLastWeekDates.length === 0) return null;
        const missing = markCtx.requiredLastWeekDates.filter((d) => !timePeriods.includes(d));
        if (missing.length === 0) return null;
        const sorted = [...missing].sort();
        return { startDate: sorted[0], endDate: sorted[sorted.length - 1] };
    }, [markCtx, timePeriods]);

    const { data: markLastWeekData } = useQuery<QuerySortingDashboardResponse>(
        isUsingV2 ? QUERY_SORTING_DASHBOARD_V2 : QUERY_SORTING_DASHBOARD,
        {
            variables: {
                input: isUsingV2 ? {
                    queryType: ScaleQueryType.DAILY as any,
                    startDate: markLastWeekRange?.startDate,
                    endDate: markLastWeekRange?.endDate,
                    startHour: 0,
                    endHour: 23,
                    todayRealtime: false,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                } : {
                    queryType: ScaleQueryType.DAILY as any,
                    startDate: markLastWeekRange?.startDate,
                    endDate: markLastWeekRange?.endDate,
                    hubId: selectedHubId,
                    businessType: currentBusinessType,
                    customerName: selectedCustomerName === '__other__' ? undefined : selectedCustomerName ?? undefined,
                    customerNames: sliceCustomerNames,
                },
            },
            skip: !markLastWeekRange,
            fetchPolicy: 'cache-first',
        }
    );

    /** 上周同日数据按 `${sortingCode|hubId}-${timePeriod}` 索引；取值走与主表一致的 merge + 字段映射 */
    const markLastWeekValueOf = useCallback(
        (row: TableDataRow, field: string, date: string): unknown => {
            const items = markLastWeekData?.querySortingDashboard?.items;
            if (!items || items.length === 0) return undefined;
            const matched = items.filter((i: SortingDashboardItem) => {
                if (i.timePeriod !== date) return false;
                return row.sortingCode == null
                    ? String(i.hubId) === String(row.drillHubId ?? 0)
                    : i.sortingCode === row.sortingCode;
            });
            if (matched.length === 0) return undefined;
            return getMetricFieldValue(mergeSortingDashboardItemsForDisplay(matched), field);
        },
        [markLastWeekData, getMetricFieldValue]
    );

    /** 计算单元格标记；上周同日优先取表内已有列，缺失才用补充请求的结果 */
    const resolveMarkForCell = useCallback(
        (row: TableDataRow, period: string): CellMark => {
            const field = row.metricType;
            if (!markCtx.ruleByField.has(field)) return NO_CELL_MARK;
            const lastWeekDate = markCtx.lastWeekDateByField.get(field);
            let lastWeekValue: unknown;
            if (lastWeekDate) {
                lastWeekValue = (row as Record<string, unknown>)[lastWeekDate];
                if (lastWeekValue === undefined) {
                    lastWeekValue = markLastWeekValueOf(row, field, lastWeekDate);
                }
            }
            return resolveCellMark({
                ctx: markCtx,
                fieldName: field,
                columnDate: period,
                row: {
                    isNation: row.sortingCode == null,
                    hubLabel: row.sortingCode == null ? undefined : formatHubDisplayLabel(row.sortingCode),
                },
                cellValue: (row as Record<string, unknown>)[period],
                lastWeekValue,
            });
        },
        [markCtx, markLastWeekValueOf]
    );

    const sourceExpandableKeys = useMemo(() => {
        const keys: string[] = [];
        const groupMetricKeys = topLevelMetricConfigs
            .filter(config => config.isGroup)
            .map(config => config.key);

        if (selectedHubId === 0 && hasFullWarehouseAccess) {
            keys.push('inbound-main');
            groupMetricKeys.forEach(key => keys.push(`${key}-main`));
        }

        allSortingCodesForExpansion.forEach((sortingCode: string) => {
            const baseKey = `inbound-${sortingCode}`;
            keys.push(baseKey);
            groupMetricKeys.forEach(key => keys.push(`${key}-${sortingCode}`));
        });

        return keys;
    }, [allSortingCodesForExpansion, selectedHubId, hasFullWarehouseAccess, topLevelMetricConfigs]);

    useEffect(() => {
        if (sourceExpandableKeys.length === 0) {
            prevExpandableKeysRef.current = [];
            return;
        }

        const prevKeys = prevExpandableKeysRef.current;
        const keysSame =
            prevKeys.length === sourceExpandableKeys.length &&
            sourceExpandableKeys.every((k, i) => prevKeys[i] === k);

        if (!keysSame) {
            setExpandedMetrics(new Set(sourceExpandableKeys));
            prevExpandableKeysRef.current = sourceExpandableKeys;
        }
    }, [sourceExpandableKeys]);

    const annotationDateKeys = useMemo(
        () => Array.from(new Set(timePeriods.map((period) => computeAnnotationKey(period, timeRange).dateKey))),
        [timePeriods, timeRange]
    );
    const {
        annotationsByKey,
        createAnnotation: createDashboardAnnotation,
        updateAnnotation: updateDashboardAnnotation,
        deleteAnnotation: deleteDashboardAnnotation,
        refetchAnnotations: refetchSortingAnnotations,
    } = useDashboardAnnotations({ pageKey: PAGE_KEY_SORTING, dateKeys: annotationDateKeys });

    const columns: ColumnsType<TableDataRow> = useMemo(() => {
        const cols: ColumnsType<TableDataRow> = [
            {
                title: '指标',
                dataIndex: 'metricName',
                key: 'metricName',
                fixed: 'left',
                width: 250,
                render: (text: string, record: TableDataRow) => {
                    const leftPadding = record.depth * 20;
                    return (
                        <div className="flex items-center gap-2" style={{ paddingLeft: leftPadding }}>
                            {record.isExpandable ? (
                                <span
                                    className="cursor-pointer text-blue-500 flex-shrink-0 inline-flex w-6 justify-center"
                                    onClick={() => toggleExpand(record.key)}
                                >
                                    {expandedMetrics.has(record.key) ? (
                                        <DownOutlined />
                                    ) : (
                                        <RightOutlined />
                                    )}
                                </span>
                            ) : record.depth > 0 ? (
                                <span className="inline-block w-6 flex-shrink-0" aria-hidden />
                            ) : null}
                            <span className={record.depth === 0 ? 'font-medium' : 'text-gray-600'}>
                                {text}
                                {record.metricTooltip && <MetricTooltip content={record.metricTooltip} />}
                            </span>
                        </div>
                    );
                },
            },
        ];

        // 非全天日期列需要在列头加「该天不满24小时」提示：仅起始日 startHour>0 或结束日 endHour<23 时标记该列
        const isPartialDayColumn = (p: string): boolean => {
            if (!shouldHideSortingEfficiencyMetrics) return false;
            if (isDashboardAvgColumnLabel(p) || p === '环比') return false;
            if (timeRange?.todayRealtime) return true;
            const startH = timeRange?.startHour ?? 0;
            const endH = timeRange?.endHour ?? 23;
            const sd = timeRange?.startDate;
            const ed = timeRange?.endDate;
            if (p === sd && startH > 0) return true;
            if (p === ed && endH < 23) return true;
            if (p === sd && p === ed) return startH > 0 || endH < 23;
            return false;
        };

        timePeriods.forEach(period => {
            let titleContent: React.ReactNode = period;

            if (period.includes('~')) {
                if (period.includes('环比')) {
                    const parts = period.split(' ');
                    const dateRange = parts[0];
                    const [start, end] = dateRange.split('~');
                    titleContent = (
                        <div className="flex flex-col items-center leading-tight">
                            <div>{start}~</div>
                            <div>{end}</div>
                            <div>周环比</div>
                        </div>
                    );
                } else {
                    const [start, end] = period.split('~');
                    titleContent = (
                        <div className="flex flex-col items-center leading-tight">
                            <div>{start}~</div>
                            <div>{end}</div>
                        </div>
                    );
                }
            } else if (period === '环比') {
                titleContent = queryType === ScaleQueryType.DAILY ? '周环比' : '环比';
            }

            if (isPartialDayColumn(period)) {
                titleContent = (
                    <span className="inline-flex items-center gap-1">
                        {titleContent}
                    </span>
                );
            }

            cols.push({
                title: titleContent,
                dataIndex: period,
                key: period,
                width: 160,
                align: 'right',
                render: (value: any, record: TableDataRow) => {
                    const { columnType, dateKey } = computeAnnotationKey(period, timeRange);
                    const annotationCellCtx: CellContext = {
                        businessType: String(currentBusinessType),
                        hubId: String(record.drillHubId ?? (record.sortingCode ? sortingCodeToHubId.get(record.sortingCode) ?? 0 : 0)),
                        metricKey: record.metricType,
                        columnType,
                        dateKey,
                    };
                    const annotationDims = sortingAnnotationConfig.buildCellDimensions(annotationCellCtx);
                    const cellAnnotations = annotationsByKey.get(
                        buildAnnotationKey(PAGE_KEY_SORTING, annotationDims, annotationCellCtx.dateKey, annotationCellCtx.columnType, annotationCellCtx.hubId ?? '', annotationCellCtx.metricKey)
                    );
                    const withAnnotation = (node: React.ReactNode) => (
                        <MetricAnnotationCell
                            hubId={annotationCellCtx.hubId ?? ''}
                            metricKey={annotationCellCtx.metricKey}
                            dimensions={annotationDims}
                            dateKey={annotationCellCtx.dateKey}
                            columnType={annotationCellCtx.columnType}
                            annotations={cellAnnotations}
                            writeResource={sortingAnnotationConfig.writeResource}
                            adminResource={sortingAnnotationConfig.adminResource}
                            onCreate={createDashboardAnnotation}
                            onUpdate={updateDashboardAnnotation}
                            onDelete={deleteDashboardAnnotation}
                            onOpen={refetchSortingAnnotations}
                        >
                            {node}
                        </MetricAnnotationCell>
                    );

                    if (value === undefined || value === null) {
                        return withAnnotation('-');
                    }

                    if (period === '环比') {
                        return withAnnotation(formatChange(value, record.metricType));
                    }

                    const markTextClass = resolveMarkForCell(record, period).redText
                        ? 'dashboard-mark-text-danger'
                        : undefined;
                    const cellContent =
                        typeof value === 'string' ? (
                            <span className={markTextClass}>
                                {isPlainBackendPercentCellValue(value) ? formatPercentage(value) : value}
                            </span>
                        ) : typeof value === 'number' ? (
                            <span className={markTextClass}>{formatNumber(value)}</span>
                        ) : (
                            '-'
                        );

                    const isAvgColumn = isDashboardAvgColumnLabel(period);
                    const canDrillByPeriod = period !== '环比' && isPeriodWithinLast30Days(period, queryType);
                    const isDrillable =
                        drillMetricTypes.includes(record.metricType as SortingDashboardDrillType) &&
                        value !== '-' &&
                        !isAvgColumn &&
                        canDrillByPeriod &&
                        isNonZeroForDrill(value);
                    if (isDrillable) {
                        return withAnnotation(
                            <span
                                className="cursor-pointer underline decoration-purple-600 hover:decoration-purple-700"
                                onClick={() => {
                                    const { start, end } = periodToDateRange(period, queryType);
                                    setDrawerStatDateStart(start);
                                    setDrawerStatDateEnd(end);
                                    // 跨午夜窗口（startHour > endHour）下，被点击列的有效小时窗需按
                                    // 该列是否为窗口起始日 / 结束日裁剪；否则后端会按"同一天 8:00-7:59"过滤导致空集
                                    if (isUsingV2 && timeRange) {
                                        const rangeStartHour = timeRange.todayRealtime ? 0 : (timeRange.startHour ?? 0);
                                        const rangeEndHour = timeRange.todayRealtime
                                            ? dashboardNowInPacific().hour()
                                            : (timeRange.endHour ?? 23);
                                        const isFirstDay = start === timeRange.startDate;
                                        const isLastDay = end === timeRange.endDate;
                                        setDrawerWindowStartHour(isFirstDay ? rangeStartHour : 0);
                                        setDrawerWindowEndHour(isLastDay ? rangeEndHour : 23);
                                    } else {
                                        setDrawerWindowStartHour(0);
                                        setDrawerWindowEndHour(23);
                                    }
                                    const isAllUsRow = record.key === 'inbound-main' || (typeof record.key === 'string' && record.key.endsWith('-main'));
                                    const nationalAllHubs = selectedHubId === 0 && hasFullWarehouseAccess;
                                    const fallbackWhenAllHubsSelected =
                                        selectedHubId !== 0
                                            ? selectedHubId
                                            : nationalAllHubs
                                              ? 0
                                              : (singleAccessibleHubIdForDrill ?? 0);
                                    let hubIdForDrawer: number;
                                    if (isAllUsRow) {
                                        hubIdForDrawer = 0;
                                    } else if (record.sortingCode) {
                                        const explicit = record.drillHubId;
                                        if (explicit !== undefined && Number.isFinite(Number(explicit))) {
                                            hubIdForDrawer = Number(explicit);
                                        } else {
                                            hubIdForDrawer =
                                                sortingCodeToHubId.get(record.sortingCode) ?? fallbackWhenAllHubsSelected;
                                        }
                                        if (hubIdForDrawer === 0 && selectedHubId !== 0) {
                                            hubIdForDrawer = selectedHubId;
                                        } else if (
                                            hubIdForDrawer === 0 &&
                                            selectedHubId === 0 &&
                                            !nationalAllHubs &&
                                            singleAccessibleHubIdForDrill != null
                                        ) {
                                            hubIdForDrawer = singleAccessibleHubIdForDrill;
                                        }
                                    } else {
                                        // 无 sortingCode 行：总部+「全部」下钻必须传 hubId=0；已选单仓时以筛选为准
                                        if (nationalAllHubs) {
                                            hubIdForDrawer = 0;
                                        } else if (selectedHubId !== 0) {
                                            hubIdForDrawer = selectedHubId;
                                        } else {
                                            hubIdForDrawer = fallbackWhenAllHubsSelected;
                                        }
                                    }
                                    setDrawerHubId(hubIdForDrawer);
                                    setDrawerSortingCode(isAllUsRow ? null : (record.sortingCode ?? null));
                                    // FBT/CBT 拆行下钻强制按行的业务类型传 customerName，覆盖筛选器
                                    setDrawerCustomerName(
                                        record.drillCustomerName
                                            ?? (selectedCustomerName === '__other__' ? null : selectedCustomerName)
                                    );
                                    const groupPrefix = CHILD_DRILL_GROUP_TITLE[record.metricType];
                                    setDrawerTitleText(
                                        groupPrefix ? `${groupPrefix} · ${record.metricName}` : record.metricName
                                    );
                                    setDrawerDrillType(record.metricType as SortingDashboardDrillType);
                                    setDrawerVisible(true);
                                }}
                            >
                                {cellContent}
                            </span>
                        );
                    }
                    return withAnnotation(cellContent);
                },
                onCell: (record: TableDataRow) => ({
                    className: ['group/annot', markCellClassName(resolveMarkForCell(record, period))]
                        .filter(Boolean)
                        .join(' '),
                }),
            });
        });

        return cols;
    }, [
        timePeriods,
        expandedMetrics,
        resolveMarkForCell,
        selectedHubId,
        sortingCodeToHubId,
        queryType,
        drillMetricTypes,
        singleAccessibleHubIdForDrill,
        timeRange,
        currentBusinessType,
        selectedCustomerName,
        annotationsByKey,
        createDashboardAnnotation,
        updateDashboardAnnotation,
        deleteDashboardAnnotation,
    ]);

    const handleDownloadSorting = useCallback(async () => {
        if (!timeRange) {
            message.warning('请先选择时间范围');
            return;
        }
        if (!tableData || tableData.length === 0 || !timePeriods || timePeriods.length === 0) {
            message.warning('当前条件下无分拣数据可导出');
            return;
        }
        if (exportLoading) {
            message.warning('导出任务生成中，请稍候');
            return;
        }

        /** 与规模看板一致：按仓库类型区分文件名与工作表名 */
        const isSortingHubExport = businessTypeTab === 'sortingHub';
        const fileNamePrefix = isSortingHubExport
            ? 'Sorting_Dashboard_SortingHub'
            : 'Sorting_Dashboard_LastMileHub';
        const sortingBreakMainSheetName = isSortingHubExport
            ? '分拣数据（分拣仓）'
            : '分拣数据（尾程仓）';
        const exportQueryType = timeRange.queryType as ScaleQueryType;
        const exportPeriodLabels = timePeriods.map((p) =>
            p === '环比' && exportQueryType === ScaleQueryType.DAILY ? '周环比' : p
        );
        const headers = ['指标', ...exportPeriodLabels];
        const mainRows = tableData.map((row) =>
            headers.map((h) => {
                if (h === '指标') {
                    return sortingDashboardExportCellString(row.metricName);
                }
                const dataKey = h === '周环比' && exportQueryType === ScaleQueryType.DAILY ? '环比' : h;
                return sortingDashboardExportCellString((row as Record<string, unknown>)[dataKey]);
            })
        );

        await runExportWithCooldown(async () => {
            try {
                const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const auditSummary = `sorting businessType=${businessTypeTab} queryType=${timeRange.queryType} ${timeRange.startDate}..${timeRange.endDate} hubId=${selectedHubId}`;
                const response = await generateDashboardTableExport({
                    variables: {
                        input: {
                            fileNamePrefix,
                            timezone: userTimezone,
                            snapshotSheetName: sortingBreakMainSheetName,
                            mainSheetName: sortingBreakMainSheetName,
                            snapshotRows: null,
                            mainColumnHeaders: headers,
                            mainRows,
                            auditSummary,
                            warehouseId: selectedHubId !== 0 ? selectedHubId : null,
                        },
                    },
                });
                const fileName = response.data?.generateDashboardTableExport;
                if (fileName) {
                    setGeneratedFileName(fileName);
                    setDownloadCompleteModalVisible(true);
                } else {
                    message.error('导出失败，请稍后重试');
                }
            } catch (error: unknown) {
                showError(error);
            }
        });
    }, [
        timeRange,
        tableData,
        timePeriods,
        selectedHubId,
        businessTypeTab,
        generateDashboardTableExport,
        exportLoading,
        runExportWithCooldown,
    ]);

    return (
        <div className="p-6 min-h-full flex flex-col">
            <Card
                title={
                    <span className="text-xl font-semibold">
                        {t('dashboard.sorting.pageTitle')}
                        <span className="text-sm font-normal ml-3">（时区说明: 日期均为美西时区；明细下钻只支持最近30天{latestCalculatedAt && `；数据更新于: ${formatCalculatedAt(latestCalculatedAt)}`}）</span>
                    </span>
                }
                bordered={false}
                className="flex-1"
            >
                {/* 两行筛选区合并到同一个 grid，TimeRangeFilter 内部也使用相同规格的 grid-cols-3 gap-4，列宽自动对齐 */}
                {/* max-w 限制大屏下不过度拉伸；小屏 grid 仍按 1fr 等分 */}
                <div className="mb-4 grid grid-cols-3 gap-4 gap-y-3 max-w-screen-xl">
                    {/* 第一行：TimeRangeFilter 占满 3 列 */}
                    <div className="col-span-3">
                        <TimeRangeFilter
                            onChange={handleTimeRangeChange}
                            maxDaysBack={365}
                            showHourRange={true}
                            dashboardTimezone={DASHBOARD_PACIFIC_TZ}
                        />
                    </div>
                    {/* 第二行：分拣仓 / 客户名称 */}
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 whitespace-nowrap">分拣仓名称</span>
                        <Select
                            className="flex-1 min-w-0"
                            value={hubSelectValue}
                            onChange={handleHubChange}
                            options={hubOptions}
                            loading={hubsLoading}
                            placeholder="选择分拣仓"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-800 whitespace-nowrap">
                            {businessTypeTab === 'sortingHub' ? '业务类型' : '客户名称'}
                        </span>
                        <Select
                            className="flex-1 min-w-0"
                            value={selectedCustomerName}
                            onChange={(v) => setSelectedCustomerName(v)}
                            options={customerNameOptions}
                            placeholder="全部"
                        />
                    </div>
                </div>

                <SortingOrderFlowSegmented
                    value={businessTypeTab}
                    onChange={setBusinessTypeTab}
                    extra={
                        <Button
                            type="primary"
                            onClick={() => void handleDownloadSorting()}
                            loading={exportLoading}
                            disabled={exportLoading}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">{getExportButtonLabel()}</span>
                        </Button>
                    }
                />

                {shouldHideSortingEfficiencyMetrics && (
                    <div className="mb-4">
                        <div className="p-3 rounded text-sm font-bold">
                            {timeRange?.todayRealtime ? '今日实时数据截止当前美西时间，数据未完整，仅供参考；不展示分拣时效和分拣峰值' : '非完整一天（00-23）不展示分拣时效和分拣峰值'}
                        </div>
                    </div>
                )}

                {businessTypeTab !== 'sortingHub' && (
                    <DashboardMarkLegend ctx={markCtx} metricLabels={SORTING_MARK_METRIC_LABELS} />
                )}

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        rowKey="key"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        sticky
                        bordered
                        size="middle"
                        className="sorting-dashboard-table"
                    />
                </Spin>
            </Card>
            {drawerDrillType && drawerStatDateStart && drawerStatDateEnd && (
                <SortingDashboardDetailDrawer
                    visible={drawerVisible}
                    dashboardVariant="sorting"
                    onClose={() => {
                        setDrawerVisible(false);
                        setDrawerDrillType(null);
                        setDrawerHubId(0);
                        setDrawerStatDateStart('');
                        setDrawerStatDateEnd('');
                        setDrawerTitleText('');
                        setDrawerCustomerName(null);
                        setDrawerSortingCode(null);
                        setDrawerWindowStartHour(0);
                        setDrawerWindowEndHour(23);
                    }}
                    title={drawerTitleText}
                    drillType={drawerDrillType}
                    statDateStart={drawerStatDateStart}
                    statDateEnd={drawerStatDateEnd}
                    hubId={drawerHubId}
                    hubList={hubListForDrawer}
                    businessType={
                        businessTypeTab === 'sortingHub' ? WarehouseBusinessType.SORTING : WarehouseBusinessType.LAST_MILE
                    }
                    customerName={drawerCustomerName}
                    windowStartHour={drawerWindowStartHour}
                    windowEndHour={drawerWindowEndHour}
                    hubSortingCode={drawerSortingCode}
                />
            )}
            <AsyncReportDownloadModal
                visible={downloadCompleteModalVisible}
                onClose={() => setDownloadCompleteModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.sorting}
            />
        </div>
    );
};

function formatCalculatedAt(iso: string): string {
    try {
        return dayjs(iso).tz(DASHBOARD_PACIFIC_TZ).format('YYYY-MM-DD HH:mm');
    } catch {
        return iso;
    }
}

export default SortingSharedPage;